import { Button, Form, Input, InputNumber, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import FormItem from 'antd/es/form/FormItem';
import FormList from 'antd/es/form/FormList';
import { DatePicker } from 'antd/lib';
import { ProviderSelectorContainer } from '../shared/provider-selector/provider-selector.containers';
import { map } from 'lodash';
import { Dayjs } from 'dayjs';
import { useImperativeHandle, useState, useEffect } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { ProductsApi } from '../utils/api-client';

type EnterpriseOrderFormComponentProps = {
  formRef?: React.RefObject<any>;
};

export const ProductSelector = ({
  providerId,
  value,
  onChange,
  onProductSelect,
}: {
  providerId?: string;
  value?: string;
  onChange?: (value: string) => void;
  onProductSelect?: (product: any) => void;
}) => {
  const [products, setProducts] = useState<any[]>([]);

  useEffect(() => {
    if (providerId) {
      new ProductsApi()
        .productControllerGetProducts(
          undefined,
          undefined,
          [providerId],
          undefined,
          undefined,
          undefined
        )
        .then((res) => {
          setProducts(res.data.products);
        });
    } else {
      setProducts([]);
    }
  }, [providerId]);

  return (
    <Select
      value={value}
      onChange={(val) => {
        onChange?.(val);
        const product = products.find((p) => p.id === val);
        if (product && onProductSelect) {
          onProductSelect(product);
        }
      }}
      placeholder="請選擇品項"
      showSearch
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      options={products.map((p) => ({
        label: p.name,
        value: p.id,
      }))}
    />
  );
};

export const EnterpriseOrderFormComponent: React.FC<
  EnterpriseOrderFormComponentProps
> = ({ formRef }) => {
  const [form] = useForm<{
    clientName: string;
    clientTaxId: string;
    contactInfo: string;
    deliveryAddress: string;
    deliveryDate: Dayjs;
    deliveryFee: number;
    discount: number;
    items: {
      providerId: string;
      name: string;
      quantity: number;
      price: number;
    }[];
  }>();

  useImperativeHandle(formRef, () => form, [form]);

  return (
    <Form form={form} preserve={false}>
      <FormItem
        label="訂餐日期"
        name="deliveryDate"
        rules={[{ required: true, message: '請輸入訂餐日期' }]}
      >
        <DatePicker
          placeholder="請選擇訂餐日期"
          className="z-50 w-full"
          showTime
          showHour
          showMinute
          getPopupContainer={(triggerNode) => {
            return triggerNode.parentElement
              ? triggerNode.parentElement
              : triggerNode;
          }}
        />
      </FormItem>
      <div className="flex gap-2 *:flex-grow *:basis-0">
        <FormItem
          label="運費"
          name="deliveryFee"
          rules={[
            { required: true, message: '請輸入運費' },
            { type: 'integer', message: '運費必須是整數' },
          ]}
        >
          <InputNumber placeholder="請輸入運費" min={0} className="w-full" />
        </FormItem>
        <FormItem
          label="折扣(%)"
          name="discount"
          rules={[{ required: true, message: '請輸入折扣' }]}
        >
          <InputNumber
            placeholder="請輸入折扣"
            min={0}
            max={100}
            className="w-full"
            step={5}
          />
        </FormItem>
      </div>
      <div className="flex gap-2 *:flex-grow">
        <FormItem
          label="客戶名稱"
          name="clientName"
          rules={[{ required: true, message: '請輸入客戶名稱' }]}
        >
          <Input placeholder="請輸入客戶名稱" />
        </FormItem>
        <FormItem
          label="統一編號"
          name="clientTaxId"
          rules={[
            {
              required: true,
              message: '請輸入統一編號',
            },
            {
              pattern: /^[0-9]{8}$/,
              message: '統一編號格式不正確',
            },
          ]}
        >
          <Input placeholder="請輸入統一編號" />
        </FormItem>
      </div>
      <FormItem
        label="訂餐地址"
        name="deliveryAddress"
        rules={[{ required: true, message: '請輸入訂餐地址' }]}
      >
        <Input placeholder="請輸入訂餐地址" />
      </FormItem>
      <FormItem
        label="聯絡窗口"
        name="contactInfo"
        rules={[{ required: true, message: '請輸入聯絡窗口' }]}
      >
        <Input placeholder="請輸入聯絡窗口" />
      </FormItem>
      <FormItem
        label="聯絡電話"
        name="contactPhone"
        rules={[{ required: true, message: '請輸入聯絡電話' }]}
      >
        <Input placeholder="請輸入聯絡電話" />
      </FormItem>
      <FormItem label="客戶來源" name="customerSource">
        <Input placeholder="請輸入客戶來源" />
      </FormItem>
      <FormList name="items">
        {(fields, { add, remove }) => {
          return (
            <>
              {map(fields, ({ key, name, ...restField }) => (
                <div key={key} className="flex flex-col">
                  <div>
                    <span className="font-bold">餐點 {name + 1}</span>
                    <Button type="link" onClick={() => remove(name)}>
                      刪除
                    </Button>
                  </div>
                  <div className="flex gap-2 *:flex-grow *:basis-0">
                    <ProviderSelectorContainer
                      isRequired
                      name={[name, 'providerId']}
                    />
                    <FormItem
                      noStyle
                      shouldUpdate={(prev, curr) => {
                        return (
                          prev.items?.[name]?.providerId !==
                          curr.items?.[name]?.providerId
                        );
                      }}
                    >
                      {({ getFieldValue, setFieldsValue }) => {
                        const providerId = getFieldValue([
                          'items',
                          name,
                          'providerId',
                        ]);
                        return (
                          <>
                            <FormItem
                              {...restField}
                              name={[name, 'productId']}
                              label="品項名稱"
                              rules={[{ required: true, message: '請選擇品項' }]}
                              className="flex-grow"
                            >
                              <ProductSelector
                                providerId={providerId}
                                onProductSelect={(product) => {
                                  const items = getFieldValue('items');
                                  if (items && items[name]) {
                                    items[name].price =
                                      product.salePrice ||
                                      product.inStorePrice ||
                                      0;
                                    items[name].name = product.name;
                                    setFieldsValue({ items: [...items] });
                                  }
                                }}
                              />
                            </FormItem>
                            <FormItem name={[name, 'name']} hidden>
                              <Input />
                            </FormItem>
                          </>
                        );
                      }}
                    </FormItem>
                  </div>
                  <div className="flex gap-2 *:flex-grow *:basis-0">
                    <FormItem
                      {...restField}
                      name={[name, 'price']}
                      label="金額"
                      className="flex-shrink-0"
                      rules={[{ required: true, message: '請輸入金額' }]}
                    >
                      <InputNumber
                        placeholder="請輸入金額"
                        min={1}
                        className="w-full"
                      />
                    </FormItem>
                    <FormItem
                      {...restField}
                      name={[name, 'quantity']}
                      label="數量"
                      className="flex-shrink-0"
                      rules={[{ required: true, message: '請輸入數量' }]}
                    >
                      <InputNumber
                        placeholder="數量"
                        min={1}
                        className="w-full"
                      />
                    </FormItem>
                  </div>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                新增餐點
              </Button>
            </>
          );
        }}
      </FormList>
    </Form>
  );
};
